/* Staff Home — landing page for users with role `staff_user`.
 * Resolves the linked staff record via GET /api/staff/me and dispatches
 * to a role-specific dashboard (educator → attendance workflow, other
 * staff types → generic profile with quick links).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ClipboardText, ChartLine, Warning, ArrowRight, User,
  ForkKnife, Bus, Broom, ChatCircle, House, CalendarBlank,
} from '@phosphor-icons/react';

/** Icon + accent per staff_type used across the landing tiles. */
const STAFF_TYPE_META = {
  educator: { Icon: ClipboardText, label: 'Éducateur / Éducatrice', color: 'text-red-600' },
  supervisor: { Icon: Warning, label: 'Surveillant(e)', color: 'text-amber-600' },
  canteen: { Icon: ForkKnife, label: 'Cantine', color: 'text-emerald-600' },
  transport: { Icon: Bus, label: 'Transport', color: 'text-blue-600' },
  cleaning: { Icon: Broom, label: 'Entretien', color: 'text-purple-600' },
  secretariat: { Icon: ChatCircle, label: 'Secrétariat', color: 'text-blue-500' },
};

const StaffHome = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/staff/me')
      .then(({ data }) => setStaff(data))
      .catch((e) => setErr(e.response?.data?.detail || 'Impossible de charger votre profil staff'))
      .finally(() => setLoading(false));
  }, []);

  const staffType = staff?.staff_type || '';
  const meta = STAFF_TYPE_META[staffType] || { Icon: User, label: 'Personnel', color: 'text-slate-600' };
  const Icon = meta.Icon;

  // Feature tiles shown by staff_type. Common tiles are appended at the end.
  const tiles = [];
  if (staffType === 'educator') {
    tiles.push(
      { path: '/absences', label: 'Justifier les absences', desc: 'Traiter les absences non justifiées des élèves', Icon: ClipboardText, color: 'bg-red-50 hover:bg-red-100 border-red-200' },
      { path: '/attendance-stats', label: 'Statistiques de présence', desc: 'Tendances, taux d\'absence, élèves à risque', Icon: ChartLine, color: 'bg-blue-50 hover:bg-blue-100 border-blue-200' },
    );
  }
  // Generic access for all staff (profile edit lives in the main users flow)
  // iter92 — Self-service "my absences" tile available to every staff type.
  tiles.push({ path: '/my-absences', label: 'Mes absences', desc: 'Consulter et justifier vos absences avant paie', Icon: CalendarBlank, color: 'bg-amber-50 hover:bg-amber-100 border-amber-200' });
  tiles.push({ path: '/', label: 'Accueil', desc: 'Retour à la page principale', Icon: House, color: 'bg-slate-50 hover:bg-slate-100 border-slate-200' });

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="staff-home-page">
      {/* Header */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-5 flex items-start gap-4">
          <div className={`p-3 rounded-lg bg-slate-100 ${meta.color}`}>
            <Icon size={30} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wide font-semibold text-[#64748B]">
              {meta.label}
            </p>
            <h1 className="font-outfit text-2xl font-bold text-[#0F172A] mt-1" data-testid="staff-home-name">
              {staff?.first_name} {staff?.last_name}
            </h1>
            <p className="text-sm text-[#475569] mt-1">
              Bonjour {user?.name?.split(' ')[0] || staff?.first_name || 'à vous'}, bienvenue sur votre espace.
            </p>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <p className="text-sm text-[#94A3B8] text-center py-4">Chargement…</p>
      )}
      {err && (
        <Card className="border border-red-200 bg-red-50" data-testid="staff-home-error">
          <CardContent className="p-4">
            <p className="text-sm text-red-800 font-medium">{err}</p>
            <p className="text-xs text-red-700 mt-1">
              Contactez l&apos;administration pour vérifier que votre fiche personnel est bien liée à votre compte.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && !err && (
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-base">Actions à votre disposition</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="staff-home-tiles">
            {tiles.map((t) => (
              <button
                key={t.path}
                onClick={() => nav(t.path)}
                className={`text-left p-4 rounded-lg border transition ${t.color}`}
                data-testid={`staff-tile-${t.path.replace(/\//g, '-')}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <t.Icon size={20} className={meta.color} weight="duotone" />
                  <span className="font-outfit font-semibold text-[#0F172A]">{t.label}</span>
                  <ArrowRight size={14} className="ml-auto text-[#94A3B8]" />
                </div>
                <p className="text-xs text-[#64748B]">{t.desc}</p>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {!loading && !err && staffType === 'educator' && (
        <div className="text-xs text-[#94A3B8] text-center pt-2">
          En tant qu&apos;éducateur/trice, vous êtes en charge du suivi des
          absences et de la coordination avec les parents.
        </div>
      )}
    </div>
  );
};

export default StaffHome;
